import { IDX } from '@/components/posture-analysis/constants';
import type { Landmark } from '@/components/posture-analysis/types';

/** Body segments (MediaPipe BlazePose indices) for the goniometer's full-body overlay. */
export const BODY_CONNECTIONS: ReadonlyArray<readonly [number, number]> = [
  [IDX.L_SHO, IDX.R_SHO],
  [IDX.L_SHO, 13], [13, 15], // left arm: shoulder, elbow, wrist
  [IDX.R_SHO, 14], [14, 16], // right arm
  [IDX.L_SHO, IDX.L_HIP], [IDX.R_SHO, IDX.R_HIP], [IDX.L_HIP, IDX.R_HIP],
  [IDX.L_HIP, IDX.L_KNEE], [IDX.L_KNEE, IDX.L_ANK],
  [IDX.R_HIP, IDX.R_KNEE], [IDX.R_KNEE, IDX.R_ANK],
  [IDX.L_ANK, IDX.L_HEEL], [IDX.L_HEEL, IDX.L_FI], [IDX.L_ANK, IDX.L_FI],
  [IDX.R_ANK, IDX.R_HEEL], [IDX.R_HEEL, IDX.R_FI], [IDX.R_ANK, IDX.R_FI],
];

/** Points drawn as joints: ears, shoulders, elbows, wrists, hips, knees, ankles, heels, toes. */
export const BODY_JOINTS: ReadonlyArray<number> = [
  IDX.L_EAR, IDX.R_EAR, IDX.L_SHO, IDX.R_SHO, 13, 14, 15, 16,
  IDX.L_HIP, IDX.R_HIP, IDX.L_KNEE, IDX.R_KNEE, IDX.L_ANK, IDX.R_ANK,
  IDX.L_HEEL, IDX.R_HEEL, IDX.L_FI, IDX.R_FI,
];

const visible = (lm: Landmark | null | undefined, threshold: number): lm is Landmark =>
  !!lm && (lm.visibility ?? 0) > threshold;

/** Segments whose two ends are both visible, read by index (never by filtered position). */
export function skeletonSegments(
  landmarks: ReadonlyArray<Landmark | null>,
  threshold: number,
): Array<[Landmark, Landmark]> {
  const out: Array<[Landmark, Landmark]> = [];
  for (const [a, b] of BODY_CONNECTIONS) {
    const p = landmarks[a];
    const q = landmarks[b];
    if (visible(p, threshold) && visible(q, threshold)) out.push([p, q]);
  }
  return out;
}

export function skeletonJoints(landmarks: ReadonlyArray<Landmark | null>, threshold: number): Landmark[] {
  return BODY_JOINTS.map((i) => landmarks[i]).filter((lm): lm is Landmark => visible(lm, threshold));
}

/** Where a frame of `w x h` sits inside a `boxW x boxH` box with object-fit: contain. */
export function containBox(w: number, h: number, boxW: number, boxH: number) {
  const scale = Math.min(boxW / w, boxH / h);
  const width = w * scale;
  const height = h * scale;
  return { x: (boxW - width) / 2, y: (boxH - height) / 2, width, height };
}
